import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { API, setAccessToken } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { X, Loader2, Mail, Lock, User, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { trackSignUp, trackLogin } from '../lib/analytics';

export default function AuthModal({ isOpen, onClose, defaultTab = 'login' }) {
  const { login, fetchProfile } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState(defaultTab);

  // Sync tab state with defaultTab prop when modal opens
  useEffect(() => {
    if (isOpen) {
      setTab(defaultTab);
    }
  }, [isOpen, defaultTab]);
  const [loading, setLoading] = useState(false);

  // Form fields
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  if (!isOpen) return null;

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setName('');
    setRememberMe(false);
    setResetSent(false);
  };

  const handleGoogleAuth = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin + '/dashboard',
      },
    });
    if (error) {
      toast.error(error.message || 'Google sign-in failed');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      // Pass access_token directly — don't rely on onAuthStateChange firing first
      await fetchProfile(data.session.access_token);
      trackLogin('email');
      toast.success('Welcome back!');
      onClose();
      window.location.href = '/dashboard';
    } catch (err) {
      toast.error(err?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { data: { name } },
      });
      if (error) throw error;
      // Set token before calling backend so API requests are authenticated
      const token = data.session?.access_token;
      if (token) {
        setAccessToken(token);
      }
      // Create user profile in our backend
      await API.post('/auth/register-profile', { name });
      await fetchProfile(token);
      trackSignUp('email');
      toast.success('Account created! Your 7-day full-access trial has started.');
      onClose();
      window.location.href = '/dashboard';
    } catch (err) {
      toast.error(err?.message || err?.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email);
      if (error) throw error;
      setResetSent(true);
      toast.success('If an account exists, a reset link has been sent');
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="auth-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-md p-8 shadow-2xl shadow-black/40 animate-scale-in">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-500 hover:text-slate-300 transition-colors"
          data-testid="auth-modal-close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Logo */}
        <div className="flex justify-center mb-6">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center shadow-lg shadow-orange-500/20">
              <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><rect x="3" y="3" width="18" height="18" rx="2" strokeWidth="2" /></svg>
            </div>
            <span className="font-heading font-bold text-lg text-white">SquareCount</span>
          </div>
        </div>

        {/* Tab switcher */}
        {tab !== 'forgot' && (
          <div className="flex gap-1 mb-6 bg-slate-700/50 rounded-lg p-1">
            <button
              onClick={() => { setTab('login'); resetForm(); }}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                tab === 'login' ? 'bg-orange-500 text-white' : 'text-slate-400 hover:text-white'
              }`}
              data-testid="tab-login"
            >
              Sign In
            </button>
            <button
              onClick={() => { setTab('register'); resetForm(); }}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                tab === 'register' ? 'bg-orange-500 text-white' : 'text-slate-400 hover:text-white'
              }`}
              data-testid="tab-register"
            >
              Sign Up
            </button>
          </div>
        )}

        {/* Login form */}
        {tab === 'login' && (
          <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
            <h2 className="text-xl font-bold text-white text-center mb-1">Welcome back</h2>
            <p className="text-slate-400 text-sm text-center mb-4">Sign in to your account</p>

            <div className="relative">
              <Mail className="absolute left-3 top-3.5 w-4 h-4 text-slate-400" />
              <input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full bg-slate-800 border border-slate-700 text-white rounded-lg py-3 pl-10 pr-4 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent placeholder-slate-500"
                data-testid="login-email"
              />
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-3.5 w-4 h-4 text-slate-400" />
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full bg-slate-800 border border-slate-700 text-white rounded-lg py-3 pl-10 pr-4 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent placeholder-slate-500"
                data-testid="login-password"
              />
            </div>
            <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="remember-me-label">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-orange-500 focus:ring-orange-500 focus:ring-offset-0 cursor-pointer"
                data-testid="remember-me-checkbox"
              />
              <span className="text-slate-400 text-sm">Remember me for 30 days</span>
            </label>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-orange-500 hover:bg-orange-400 text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="login-submit"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Sign In
            </button>
            <button
              type="button"
              onClick={() => { setTab('forgot'); resetForm(); }}
              className="w-full text-orange-400 hover:text-orange-300 text-sm transition-colors"
              data-testid="forgot-password-link"
            >
              Forgot your password?
            </button>
          </form>
        )}

        {/* Register form */}
        {tab === 'register' && (
          <form onSubmit={handleRegister} className="space-y-4" data-testid="register-form">
            <h2 className="text-xl font-bold text-white text-center mb-1">Start your 7-day Contractor trial</h2>
            <p className="text-slate-400 text-sm text-center mb-4">Full Contractor access — tile &amp; metal takeoffs, job costing, editable branded reports</p>

            <div className="relative">
              <User className="absolute left-3 top-3.5 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Full name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="w-full bg-slate-800 border border-slate-700 text-white rounded-lg py-3 pl-10 pr-4 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent placeholder-slate-500"
                data-testid="register-name"
              />
            </div>
            <div className="relative">
              <Mail className="absolute left-3 top-3.5 w-4 h-4 text-slate-400" />
              <input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full bg-slate-800 border border-slate-700 text-white rounded-lg py-3 pl-10 pr-4 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent placeholder-slate-500"
                data-testid="register-email"
              />
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-3.5 w-4 h-4 text-slate-400" />
              <input
                type="password"
                placeholder="Password (min 6 characters)"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="w-full bg-slate-800 border border-slate-700 text-white rounded-lg py-3 pl-10 pr-4 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent placeholder-slate-500"
                data-testid="register-password"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-orange-500 hover:bg-orange-400 text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="register-submit"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Start My Contractor Trial
            </button>
            <div className="text-xs text-slate-400 text-center mt-3 space-y-0.5">
              <p>&#10003; No credit card required &nbsp; &#10003; Cancel anytime &nbsp; &#10003; Every Contractor feature, unlimited, for 7 days</p>
            </div>
          </form>
        )}

        {/* Forgot password form */}
        {tab === 'forgot' && (
          <div data-testid="forgot-form">
            <button
              onClick={() => { setTab('login'); resetForm(); }}
              className="text-slate-400 hover:text-white text-sm mb-4 flex items-center gap-1 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" /> Back to sign in
            </button>
            <h2 className="text-2xl font-bold text-white mb-1">Reset password</h2>
            <p className="text-slate-400 text-sm mb-6">
              {resetSent
                ? 'Check your email for a reset link.'
                : "Enter your email and we'll send you a reset link."}
            </p>
            {!resetSent ? (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div className="relative">
                  <Mail className="absolute left-3 top-3.5 w-4 h-4 text-slate-400" />
                  <input
                    type="email"
                    placeholder="Email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full bg-slate-800 border border-slate-700 text-white rounded-lg py-3 pl-10 pr-4 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent placeholder-slate-500"
                    data-testid="forgot-email"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-orange-500 hover:bg-orange-400 text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="forgot-submit"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Send Reset Link
                </button>
              </form>
            ) : (
              <div className="text-center text-slate-300 bg-slate-700/50 rounded-lg p-4">
                Reset link sent to <span className="text-orange-400 font-medium">{email}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
